import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Video, Mic, Play, Pause, Loader2, ArrowRight, Circle, Square,
  CheckCircle2, AlertTriangle, MessageSquare, Lock, Zap, Sparkles,
  Clock, TrendingUp, TrendingDown, ChevronRight, RefreshCw, Share2, Volume2, Target,
} from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import PresetJobPicker from "@/components/PresetJobPicker";
import InterviewAvatar from "@/components/InterviewAvatar";
import ShareAssessmentModal from "@/components/ShareAssessmentModal";
import FreeSummaryScreen from "@/components/FreeSummaryScreen";
import UpgradeLiveModal from "@/components/UpgradeLiveModal";
import CareerMilestoneBanner from "@/components/CareerMilestoneBanner";
import ScoreInfoTip from "@/components/ScoreInfoTip";
import ContextualFeedback from "@/components/ContextualFeedback";
import DeliverableFeedback from "@/components/DeliverableFeedback";
import ReportProgressSteps from "@/components/ReportProgressSteps";
import { MicLevelMeter, UploadStages, permissionErrorMessage } from "@/components/RecorderKit";
import { buildInterviewCompleteMilestone } from "@/lib/milestones";
import { useEntitlement } from "@/lib/entitlement-context";
import { useAuth } from "@/lib/auth-context";
import GuestGate from "@/components/GuestGate";
import { useWorkspace } from "@/lib/workspace-context";
import { WorkspaceMismatchBanner, WorkspaceMismatchDialog } from "@/components/WorkspaceMismatch";
import { ActiveSessionDialog } from "@/components/ActiveSessionDialog";
import { track as trackEvent, AnalyticsEvent } from "@/lib/analytics";

const RECOMMENDATION = {
  ready: { color: "emerald", dot: "🟢", label: "Ready for Interview" },
  needs_practice: { color: "amber", dot: "🟡", label: "Needs More Practice" },
  not_ready: { color: "rose", dot: "🔴", label: "Not Yet Ready" },
};

const RECOMMENDATION_CLASSES = {
  emerald: "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-300",
  amber: "border-amber-500/40 bg-amber-500/[0.06] text-amber-300",
  rose: "border-rose-500/40 bg-rose-500/[0.06] text-rose-300",
};

const TAG_STYLES = {
  strength: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  example: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  filler: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  pace: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  structure: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  presence: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  improvement: "border-zinc-700 bg-zinc-800/60 text-zinc-300",
};

function fmtTime(s) {
  const n = Math.max(0, Math.round(s || 0));
  const m = Math.floor(n / 60);
  const r = String(n % 60).padStart(2, "0");
  return `${m}:${r}`;
}

/* -------------------------- Locked preview -------------------------- */

function LockedPreview() {
  return (
    <div data-testid="video-locked" className="max-w-4xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">03 · Practice · Premium</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Mock Interview</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Practice before it counts. Rehearse on camera as many times as you need, get a full Executive
          Interview Report scored across communication, confidence, answer structure, job fit, and
          leadership presence, then take the Official AI Interview when you&apos;re ready.
        </p>
      </div>

      <div className="relative rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/50 to-zinc-950/70 p-8 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className="h-32 w-40 rounded-xl border border-zinc-800 bg-zinc-950/60 flex items-center justify-center relative overflow-hidden">
            <Lock className="h-8 w-8 text-amber-400" />
            <div className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest text-zinc-600">Locked</div>
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/25 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
              <Sparkles className="h-3 w-3" /> Premium feature
            </div>
            <h2 className="mt-3 text-xl md:text-2xl font-bold text-zinc-50">
              Unlock live video practice with an executive-grade scorecard.
            </h2>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-300">
              {[
                "Official AI Interview persona",
                "Webcam + mic recording",
                "Full transcript + timeline",
                "Communication · Confidence · Job Fit",
                "STAR / Executive Presence scoring",
                "AI Interview Replay with pinned comments",
                "Hiring recommendation",
                "Attempt-over-attempt comparison",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/app/billing"
                data-testid="video-locked-unlock"
                className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)]"
              >
                <Zap className="h-3.5 w-3.5" />
                Try Interview Ready — free during beta
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-sm border border-zinc-800 text-zinc-100 hover:bg-zinc-900 rounded-lg px-4 py-2.5"
              >
                Compare plans <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Setup step -------------------------- */

function SetupPanel({ onStart, isPaid }) {
  const { user: authUser } = useAuth();
  const isGuestUser = !!authUser?.is_guest;
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [jd, setJd] = useState("");
  const [personaId, setPersonaId] = useState("hiring_manager");
  const [personas, setPersonas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resume, setResume] = useState(null);
  const [mismatchDetail, setMismatchDetail] = useState(null);
  const [mismatchDismissed, setMismatchDismissed] = useState(false);
  const [activeDetail, setActiveDetail] = useState(null);
  const [selectedSetId, setSelectedSetId] = useState(null);
  const { setActive, activeId: wsActiveId, loading: wsLoading } = useWorkspace();
  const [setupParams] = useSearchParams();
  const [noPrepData, setNoPrepData] = useState(false);
  const [prepSets, setPrepSets] = useState([]);
  const autoStartRef = useRef(false);

  // Step 03 focuses on practicing what was prepared in Step 02. Load the
  // prepared sets for one-click reuse; if none exist, point back to Step 02.
  // Wait for workspace hydration (header presence) and reload on switch so
  // the list never shows another workspace's sets.
  useEffect(() => {
    if (wsLoading) return;
    api.get("/interview/questions/sets")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setNoPrepData(list.length === 0);
        setPrepSets(list.slice(0, 5));
      })
      .catch(() => {});
  }, [wsLoading, wsActiveId]);

  // Arriving from Step 02 with a prepared question set: start the mock on the
  // SAME questions immediately — no re-entry, no regeneration. Gated on
  // workspace hydration so the set lookup is workspace-scoped.
  useEffect(() => {
    if (wsLoading) return;
    const qsId = setupParams.get("question_set_id");
    if (!qsId || autoStartRef.current) return;
    autoStartRef.current = true;
    api.get(`/interview/questions/sets/${qsId}`)
      .then(({ data }) => {
        setRole((r) => r || data.job_title || "");
        setCompany((c) => c || data.company || "");
        setJd((j) => j || data.job_description || "");
      })
      .catch(() => {});
    _run(false, false, qsId);
  }, [wsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/video-interview/personas")
      .then(({ data }) => setPersonas(data.personas || []))
      .catch(() => {});
  }, []);

  const _run = async (confirmMismatch, replaceActive, questionSetId) => {
    setBusy(true);
    const qsId = questionSetId !== undefined ? questionSetId : selectedSetId;
    if (questionSetId !== undefined) setSelectedSetId(questionSetId);
    try {
      const { data } = await api.post("/video-interview/start", {
        job_title: role || null,
        company: company || null,
        job_description: jd,
        flexible: true,
        question_set_id: qsId || null,
        persona: personaId,
        resume_id: resume?.resume_id || null,
        confirm_workspace_mismatch: !!confirmMismatch,
        replace_active: !!replaceActive,
      });
      setMismatchDetail(null);
      setActiveDetail(null);
      onStart(data);
    } catch (e) {
      if (
        e?.response?.status === 409 &&
        e?.response?.data?.detail?.error === "workspace_mismatch"
      ) {
        setMismatchDetail(e.response.data.detail);
      } else if (
        e?.response?.status === 409 &&
        e?.response?.data?.detail?.error === "active_session"
      ) {
        setActiveDetail(e.response.data.detail);
      } else {
        setErr(formatApiError(e?.response?.data?.detail) || "Could not start the interview.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resumeActive = async () => {
    if (!activeDetail?.session_id) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/video-interview/${activeDetail.session_id}`);
      setActiveDetail(null);
      onStart(data);
    } catch {
      setErr("Could not resume the interview, please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setErr("");
    if (jd.trim().length < 40) {
      setErr("Paste a job description (at least 40 characters).");
      return;
    }
    await _run(false, false, null);
  };

  if (isGuestUser) {
    return <GuestGate feature="practice a video interview with your own resume and job description" />;
  }

  return (
    <div data-testid="video-setup" className="max-w-2xl xl:max-w-5xl space-y-5">
      <ActiveSessionDialog
        detail={activeDetail}
        busy={busy}
        onResume={resumeActive}
        onStartOver={() => _run(true, true)}
        onCancel={() => setActiveDetail(null)}
      />
      <WorkspaceMismatchDialog
        detail={mismatchDetail}
        jd={jd}
        onSwitch={() => {
          if (mismatchDetail?.suggested_workspace_id) {
            setActive(mismatchDetail.suggested_workspace_id);
            setMismatchDetail(null);
            setTimeout(() => _run(false), 100);
          }
        }}
        onCreated={() => {
          setMismatchDetail(null);
          setTimeout(() => _run(false), 150);
        }}
        onContinue={() => {
          setMismatchDetail(null);
          _run(true);
        }}
        onCancel={() => setMismatchDetail(null)}
      />
      {noPrepData && (
        <div
          data-testid="video-no-prep-banner"
          className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-xs text-zinc-300 leading-relaxed"
        >
          You haven't prepared any questions yet. Start with{" "}
          <Link to="/app/prep" className="text-amber-300 font-semibold hover:text-amber-200">
            Step 02 · Predict &amp; Prepare
          </Link>{" "}
          to predict your interview questions and build your answers, then come back here to practice them.
        </div>
      )}
      {prepSets.length > 0 && (
        <div data-testid="video-prepared-sets" className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">
            Practice your prepared questions
          </div>
          <div className="text-xs text-zinc-500 mb-3">
            Reuse the 15-question set you built in Step 02 — starts instantly, same questions.
          </div>
          <div className="space-y-1.5">
            {prepSets.map((qs) => (
              <button
                key={qs.question_set_id}
                type="button"
                disabled={busy}
                onClick={() => _run(false, false, qs.question_set_id)}
                data-testid={`video-prep-set-${qs.question_set_id}`}
                className="w-full flex items-center gap-3 text-left rounded-lg border border-zinc-800 bg-zinc-950 hover:border-amber-500/40 px-3 py-2.5 transition-colors disabled:opacity-60"
              >
                <Target className="h-4 w-4 text-amber-400 flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-zinc-100 font-medium truncate">
                    {qs.job_title || "Prepared question set"}
                    {qs.company ? <span className="text-zinc-500"> · {qs.company}</span> : null}
                  </span>
                  <span className="block text-[11px] text-zinc-500">
                    {(qs.questions?.length ?? qs.question_count ?? 15)} questions
                  </span>
                </span>
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500 flex-shrink-0" />
                ) : (
                  <ArrowRight className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-4 mb-1">
            Or start from a different job description
          </div>
        </div>
      )}
      <WorkspaceMismatchBanner
        role={role}
        company={company}
        jd={jd}
        dismissed={mismatchDismissed}
        onDismiss={() => setMismatchDismissed(true)}
      />
      <PresetJobPicker
        role={role}
        setRole={setRole}
        company={company}
        setCompany={setCompany}
        jd={jd}
        setJd={setJd}
        onResumeChange={setResume}
        testidPrefix="video"
        jdRows={8}
        showNoDocsRow={false}
      />
      {personas.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Interviewer</div>
          <div data-testid="video-persona-picker" className="flex flex-wrap gap-2">
            {personas.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPersonaId(p.id)}
                data-testid={`video-persona-${p.id}`}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                  personaId === p.id
                    ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Questions</div>
        <div
          data-testid="video-bank-note"
          className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-xs text-zinc-300 leading-relaxed"
        >
          <span className="text-zinc-100 font-semibold">15 questions · 3 batches of 5.</span>{" "}
          Introduction &amp; Motivation, Skills &amp; Experience, and Behavioral, Situational &amp; Role Fit.
          Practice any batch, any question, in any order — only what you answer is scored.
        </div>
      </div>
      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {err}
        </div>
      )}
      <div className="pt-2">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="video-start-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 text-sm shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />}
          Start mock interview
        </button>
      </div>
      <div className="text-[11px] text-zinc-600 leading-relaxed pt-1">
        We&apos;ll ask for camera and microphone access when you press start. Answers are recorded to your account only — delete them anytime from Settings &rarr; Privacy.
      </div>
    </div>
  );
}

/* -------------------------- Recording step -------------------------- */

function RecorderPanel({ session, onSession, onFinalize }) {
  const [stream, setStream] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [permissionErr, setPermissionErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [upPhase, setUpPhase] = useState(null);
  const [upPct, setUpPct] = useState(0);
  const [failedUpload, setFailedUpload] = useState(null);
  const [uploadErr, setUploadErr] = useState("");
  const [recErr, setRecErr] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const videoRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const evalTimerRef = useRef(null);
  const streamRef = useRef(null);

  const currentTurn = (session.turns || []).find((t) => t.status === "asked");
  const answered = (session.turns || []).filter((t) => t.status === "answered").length;
  const answeredPrimary = (session.turns || []).filter((t) => t.status === "answered" && !t.is_followup).length;
  const total = session.total_questions || 5;
  const questionNumber = currentTurn?.primary_index != null ? currentTurn.primary_index + 1 : Math.min(answered + 1, total);

  const startCamera = useCallback(async () => {
    setPermissionErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      setStream(s);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setPermissionErr(permissionErrorMessage(e, "camera and microphone"));
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // run once on mount

  useEffect(() => {
    const warn = (e) => {
      if (recording || uploading) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [recording, uploading]);

  const startRecording = () => {
    if (!stream || !currentTurn) return;
    setRecErr("");
    setUploadErr("");
    chunksRef.current = [];
    const hasVideo = stream.getVideoTracks && stream.getVideoTracks().length > 0;
    const candidates = hasVideo
      ? [
          "video/webm;codecs=vp9,opus",
          "video/webm;codecs=vp8,opus",
          "video/webm",
          "video/mp4;codecs=avc1,mp4a.40.2", // Safari
          "video/mp4",
        ]
      : [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/mp4;codecs=mp4a.40.2",
          "audio/mp4",
        ];
    const mime = candidates.find((m) => {
      try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
    }) || "";
    // SPEED: cap bitrate (1 Mbps video + 96 kbps audio) - browser default is ~2.5 Mbps,
    // which made 1-5 min answers 2x heavier to upload with no visible quality gain
    // for a talking-head webcam. Recording length is unchanged.
    const bitrateOpts = hasVideo
      ? { videoBitsPerSecond: 1000000, audioBitsPerSecond: 96000 }
      : { audioBitsPerSecond: 96000 };
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime, ...bitrateOpts } : bitrateOpts);
    } catch (e) {
      setRecErr("Your browser can't record this interview. Please try the latest Chrome, Edge, or Safari.");
      return;
    }
    rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => submitAnswer(mime);
    rec.onerror = (e) => {
      console.error("MediaRecorder error", e);
      setRecErr("Recording failed unexpectedly. Please try recording again.");
    };
    try {
      rec.start(1000);
    } catch (e) {
      setRecErr("Couldn't start the recorder. Refresh the page and reallow camera/mic permissions.");
      return;
    }
    setRecorder(rec);
    setRecording(true);
    setElapsed(0);
    elapsedRef.current = 0;
    timerRef.current = setInterval(() => setElapsed((s) => { elapsedRef.current = s + 1; return s + 1; }), 1000);
  };

  const stopRecording = () => {
    if (recorder && recording) {
      recorder.stop();
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecording(false);
    }
  };

  const doUpload = async (payload) => {
    if (!payload) return;
    setUploading(true);
    setUploadErr("");
    setUpPhase("uploading");
    setUpPct(0);
    try {
      const fd = new FormData();
      fd.append("video", payload.blob, `turn-${payload.turnIndex}.${payload.ext}`);
      fd.append("turn_index", String(payload.turnIndex));
      const { data } = await api.post(
        `/video-interview/${session.session_id}/answer`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000,
          onUploadProgress: (ev) => {
            const p = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setUpPct(p);
            if (p >= 100) {
              setUpPhase("transcribing");
              if (evalTimerRef.current) clearTimeout(evalTimerRef.current);
              // display heuristic: transcription time tracks answer length, evaluation follows
              evalTimerRef.current = setTimeout(
                () => setUpPhase("evaluating"),
                Math.min(20000, 2500 + (payload.duration || 30) * 250),
              );
            }
          },
        },
      );
      setFailedUpload(null);
      onSession(data.session);
    } catch (e) {
      setFailedUpload(payload);
      setUploadErr(e?.response?.data?.detail ? formatApiError(e.response.data.detail) : "Upload failed — check your connection and retry. Your recording is saved.");
    } finally {
      if (evalTimerRef.current) { clearTimeout(evalTimerRef.current); evalTimerRef.current = null; }
      setUploading(false);
      setUpPhase(null);
    }
  };

  const submitAnswer = async (mime) => {
    if (!currentTurn) return;
    const isAudio = mime && mime.startsWith("audio/");
    const isMp4 = mime && mime.includes("mp4");
    const ext = isMp4 ? (isAudio ? "m4a" : "mp4") : "webm";
    const blobType = mime ? mime.split(";")[0] : "video/webm";
    const blob = new Blob(chunksRef.current, { type: blobType });
    await doUpload({ blob, ext, turnIndex: currentTurn.index, duration: elapsedRef.current });
  };

  const finalize = async () => {
    setFinalizing(true);
    setUploadErr("");
    try {
      const { data } = await api.post(`/video-interview/${session.session_id}/finalize`);
      onFinalize(data);
    } catch (e) {
      setUploadErr(formatApiError(e?.response?.data?.detail) || "Could not finalize the interview. Please try again.");
    } finally {
      setFinalizing(false);
    }
  };

  const done = !currentTurn;

  return (
    <div data-testid="video-recorder" className="max-w-4xl grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
      {/* Camera */}
      <div className="space-y-3">
        <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden aspect-video">
          <video ref={videoRef} playsInline className="w-full h-full object-cover" data-testid="video-preview" />
          <div className="absolute right-2 bottom-2"><MicLevelMeter stream={stream} /></div>
          {recording && (
            <div
              data-testid="rec-chip"
              className={`absolute top-3 left-3 inline-flex items-center gap-2 text-xs text-white rounded-full px-3 py-1 font-medium transition-colors ${
                elapsed >= 300
                  ? "bg-amber-500/95 shadow-[0_0_20px_-4px_rgba(245,158,11,0.85)]"
                  : elapsed >= 240
                  ? "bg-amber-500/85"
                  : "bg-rose-500/90"
              }`}
            >
              <Circle className="h-2 w-2 fill-white animate-pulse" /> REC · {fmtTime(elapsed)}
              {elapsed >= 300 && (
                <span data-testid="rec-wrap-up" className="ml-1 text-[10px] uppercase tracking-widest opacity-95">
                  wrap up soon
                </span>
              )}
            </div>
          )}
          {!stream && !permissionErr && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Requesting camera…
            </div>
          )}
          {permissionErr && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 text-sm p-6 text-center">
              <Video className="h-8 w-8 text-zinc-600 mb-3" />
              <div>{permissionErr}</div>
              <button
                onClick={startCamera}
                data-testid="video-retry-perm"
                className="mt-4 text-xs border border-zinc-800 rounded-md px-3 py-1.5 hover:bg-zinc-900"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!done && !recording && !uploading && !failedUpload && (
            <button
              onClick={startRecording}
              disabled={!stream}
              data-testid="video-record-btn"
              className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-60"
            >
              <Circle className="h-3 w-3 fill-white" /> Start recording
            </button>
          )}
          {recording && (
            <button
              onClick={stopRecording}
              data-testid="video-stop-btn"
              className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2 text-sm"
            >
              <Square className="h-3 w-3 fill-zinc-950" /> Stop &amp; submit
            </button>
          )}
          {uploading && <UploadStages phase={upPhase} pct={upPct} />}
          {done && !uploading && (
            <button
              onClick={finalize}
              disabled={finalizing}
              data-testid="video-finalize-btn"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 text-sm shadow-[0_0_20px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
            >
              {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {finalizing ? "Preparing your report…" : "Generate Executive Report"}
            </button>
          )}
        </div>
        {(uploadErr || recErr) && !uploading && (
          <div data-testid="video-recorder-error" className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{uploadErr || recErr}</span>
            </div>
            {failedUpload && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={() => doUpload(failedUpload)}
                  data-testid="video-retry-upload-btn"
                  className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 text-xs"
                >
                  <RefreshCw className="h-3 w-3" /> Retry upload
                </button>
                <button
                  onClick={() => { setFailedUpload(null); setUploadErr(""); }}
                  data-testid="video-rerecord-btn"
                  className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-200 hover:bg-zinc-900 rounded-md px-3 py-1.5 text-xs"
                >
                  Re-record instead
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Question / progress */}
      <div className="space-y-3">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-2">
            Question {questionNumber} of {total}
            {currentTurn?.is_followup && (
              <span className="text-[9px] uppercase tracking-widest text-sky-300 border border-sky-500/30 bg-sky-500/[0.06] rounded-full px-1.5 py-0.5">
                Follow-up
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-zinc-100 leading-relaxed" data-testid="video-current-question">
            {currentTurn?.question || "All questions answered, click Generate Executive Report."}
          </div>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Progress</div>
          <div className="flex gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 flex-1 rounded-full ${i < answered ? "bg-amber-500" : "bg-zinc-800"}`}
              />
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">
            Tip: aim for 60–120 seconds per answer using STAR, Situation, Task, Action, Result. No time cap: record as long as you need, then tap Stop.
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Flexible mock (15 questions · 3 batches) -------------------------- */

const BATCH_META = [
  { id: 1, label: "Introduction & Motivation" },
  { id: 2, label: "Skills & Experience" },
  { id: 3, label: "Behavioral, Situational & Role Fit" },
];

function FlexibleRecorderPanel({ session, onSession, onAbort }) {
  const turns = session.turns || [];
  const answeredCount = turns.filter((t) => t.status === "answered").length;
  const pendingByBatch = [...turns]
    .filter((t) => t.status !== "answered")
    .sort((a, b) => (a.batch || 1) - (b.batch || 1) || a.index - b.index);
  const [selectedIdx, setSelectedIdx] = useState(pendingByBatch.length ? pendingByBatch[0].index : 0);
  const [stream, setStream] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [permissionErr, setPermissionErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [visemes, setVisemes] = useState(null);
  const [mouthGated, setMouthGated] = useState(false);
  const [upPhase, setUpPhase] = useState(null);
  const [upPct, setUpPct] = useState(0);
  const [failedUpload, setFailedUpload] = useState(null);
  const [uploadErr, setUploadErr] = useState("");
  const [recErr, setRecErr] = useState("");
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const evalTimerRef = useRef(null);
  const streamRef = useRef(null);

  const currentTurn = turns[selectedIdx];
  const currentBatch = BATCH_META.find((b) => b.id === (currentTurn?.batch || 1));

  const startCamera = useCallback(async () => {
    setPermissionErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      setStream(s);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setPermissionErr(permissionErrorMessage(e, "camera and microphone"));
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // run once on mount

  useEffect(() => {
    const warn = (e) => {
      if (recording || uploading) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [recording, uploading]);

  // Bank preparation: the start endpoint returns instantly while the
  // 15-question bank generates in the background — poll until it's ready
  // (or surface a clear error instead of hanging).
  useEffect(() => {
    if (!session.preparing) return;
    let alive = true;
    let tries = 0;
    const poll = async () => {
      if (!alive) return;
      try {
        const { data } = await api.get(`/video-interview/${session.session_id}`);
        if (!alive) return;
        if (!data.preparing) {
          onSession(data);
          return;
        }
      } catch { /* transient — keep polling */ }
      if (++tries < 60) {
        setTimeout(poll, 3000);
      } else if (alive) {
        onSession({
          ...session,
          preparing: false,
          status: "abandoned",
          prepare_error: "Preparation is taking longer than expected. Please try starting again.",
        });
      }
    };
    const t = setTimeout(poll, 2500);
    return () => { alive = false; clearTimeout(t); };
  }, [session.preparing, session.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The interviewer reads the selected question aloud (TTS + phoneme lip sync),
  // exactly like the Official AI Interview. Answered questions stay silent so
  // reviewing scores doesn't replay audio.
  useEffect(() => {
    if (!currentTurn || currentTurn.status === "answered") {
      if (audioRef.current) audioRef.current.pause();
      setTtsPlaying(false);
      return;
    }
    const url = `${API_BASE}/video-interview/${session.session_id}/turn/${currentTurn.index}/tts`;
    setVisemes(null);
    setMouthGated(true);
    let alive = true;
    let started = false;
    const startAudio = () => {
      if (!alive || started) return;
      started = true;
      if (audioRef.current) {
        audioRef.current.src = url;
        setTtsPlaying(true);
        audioRef.current.play().catch(() => setTtsPlaying(false));
      }
    };
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/video-interview/${session.session_id}/turn/${currentTurn.index}/visemes`);
        if (!alive) return;
        if (data.status === "ready" && Array.isArray(data.visemes)) {
          setVisemes(data.visemes);
          setMouthGated(false);
          startAudio();
          return;
        }
      } catch { /* keep polling */ }
      if (alive && ++tries < 14) setTimeout(poll, tries < 5 ? 300 : 1000);
    };
    poll();
    const playFallback = setTimeout(startAudio, 1500);
    const ungate = setTimeout(() => { if (alive) setMouthGated(false); }, 5000);
    return () => { alive = false; clearTimeout(playFallback); clearTimeout(ungate); };
  }, [selectedIdx, session.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRecording = () => {
    if (!stream || !currentTurn) return;
    if (audioRef.current) audioRef.current.pause();
    setRecErr("");
    setUploadErr("");
    chunksRef.current = [];
    const hasVideo = stream.getVideoTracks && stream.getVideoTracks().length > 0;
    const candidates = hasVideo
      ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4;codecs=avc1,mp4a.40.2", "video/mp4"]
      : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4"];
    const mime = candidates.find((m) => {
      try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
    }) || "";
    const bitrateOpts = hasVideo
      ? { videoBitsPerSecond: 1000000, audioBitsPerSecond: 96000 }
      : { audioBitsPerSecond: 96000 };
    let rec;
    try {
      rec = new MediaRecorder(stream, mime ? { mimeType: mime, ...bitrateOpts } : bitrateOpts);
    } catch {
      setRecErr("Your browser can't record this interview. Please try the latest Chrome, Edge, or Safari.");
      return;
    }
    rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => submitAnswer(mime);
    rec.onerror = (e) => {
      console.error("MediaRecorder error", e);
      setRecErr("Recording failed unexpectedly. Please try recording again.");
    };
    try {
      rec.start(1000);
    } catch {
      setRecErr("Couldn't start the recorder. Refresh the page and reallow camera/mic permissions.");
      return;
    }
    setRecorder(rec);
    setRecording(true);
    setElapsed(0);
    elapsedRef.current = 0;
    timerRef.current = setInterval(() => setElapsed((s) => { elapsedRef.current = s + 1; return s + 1; }), 1000);
  };

  const stopRecording = () => {
    if (recorder && recording) {
      recorder.stop();
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecording(false);
    }
  };

  const doUpload = async (payload) => {
    if (!payload) return;
    setUploading(true);
    setUploadErr("");
    setUpPhase("uploading");
    setUpPct(0);
    try {
      const fd = new FormData();
      fd.append("video", payload.blob, `turn-${payload.turnIndex}.${payload.ext}`);
      fd.append("turn_index", String(payload.turnIndex));
      const { data } = await api.post(
        `/video-interview/${session.session_id}/answer`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000,
          onUploadProgress: (ev) => {
            const p = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setUpPct(p);
            if (p >= 100) {
              setUpPhase("transcribing");
              if (evalTimerRef.current) clearTimeout(evalTimerRef.current);
              // display heuristic: transcription time tracks answer length, evaluation follows
              evalTimerRef.current = setTimeout(
                () => setUpPhase("evaluating"),
                Math.min(20000, 2500 + (payload.duration || 30) * 250),
              );
            }
          },
        },
      );
      setFailedUpload(null);
      onSession(data.session);
    } catch (e) {
      setFailedUpload(payload);
      setUploadErr(e?.response?.data?.detail ? formatApiError(e.response.data.detail) : "Upload failed — check your connection and retry. Your recording is saved.");
    } finally {
      if (evalTimerRef.current) { clearTimeout(evalTimerRef.current); evalTimerRef.current = null; }
      setUploading(false);
      setUpPhase(null);
    }
  };

  const submitAnswer = async (mime) => {
    if (!currentTurn) return;
    const isAudio = mime && mime.startsWith("audio/");
    const isMp4 = mime && mime.includes("mp4");
    const ext = isMp4 ? (isAudio ? "m4a" : "mp4") : "webm";
    const blobType = mime ? mime.split(";")[0] : "video/webm";
    const blob = new Blob(chunksRef.current, { type: blobType });
    await doUpload({ blob, ext, turnIndex: currentTurn.index, duration: elapsedRef.current });
  };

  return (
    <div data-testid="mock-flexible" className="max-w-5xl space-y-5">
      {session.prepare_error && (
        <div data-testid="mock-prepare-error" className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.05] p-6 text-center">
          <AlertTriangle className="h-6 w-6 text-rose-400 mx-auto mb-3" />
          <div className="text-sm text-zinc-200 font-medium">{session.prepare_error}</div>
          <button
            onClick={onAbort}
            data-testid="mock-prepare-error-back"
            className="mt-4 inline-flex items-center gap-2 text-sm bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Back to setup
          </button>
        </div>
      )}
      {!session.prepare_error && (session.preparing || turns.length === 0) && (
        <div data-testid="mock-preparing" className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-8 text-center">
          <Loader2 className="h-6 w-6 text-amber-400 animate-spin mx-auto mb-3" />
          <div className="text-sm font-semibold text-zinc-100">Preparing your 15-question mock interview…</div>
          <div className="text-xs text-zinc-500 mt-1.5 max-w-md mx-auto leading-relaxed">
            The AI is predicting the questions you're most likely to face for this role —
            3 batches of 5 covering Introduction &amp; Motivation, Skills &amp; Experience, and
            Behavioral, Situational &amp; Role Fit. This usually takes 1–2 minutes.
          </div>
        </div>
      )}
      {!session.prepare_error && !session.preparing && turns.length > 0 && (
      <>
      {/* Current Practice Score + overall progress */}
      <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400">
              {answeredCount >= turns.length ? "Final Mock Interview Score" : "Current Practice Score"}
            </div>
            <div className="mt-1 text-3xl font-bold text-zinc-50" data-testid="mock-practice-score">
              {typeof session.practice_score === "number" ? session.practice_score : "–"}
              <span className="text-base text-zinc-500">/100</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5" data-testid="mock-practice-score-basis">
              {answeredCount === 0
                ? "Answer a question to start scoring — only completed responses count."
                : `Based on ${answeredCount} completed response${answeredCount === 1 ? "" : "s"}.`}
            </div>
          </div>
          <div className="flex-1 min-w-[220px] max-w-md">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Progress</span>
              <span className="text-xs font-semibold text-zinc-200 tabular-nums" data-testid="mock-overall-progress">
                {answeredCount}/{turns.length} completed
              </span>
            </div>
            <div className="flex gap-1">
              {turns.map((t, i) => (
                <span key={i} className={`h-1.5 flex-1 rounded-full ${t.status === "answered" ? "bg-amber-500" : "bg-zinc-800"}`} />
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Your Final Mock Interview Score unlocks at 15/15.
            </div>
          </div>
        </div>
      </div>

      {/* Interview room: AI interviewer + candidate, like a real video call */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/80 p-3 sm:p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-3">
          <InterviewAvatar
            personaId={session.persona}
            speaking={ttsPlaying}
            listening={recording}
            audioRef={audioRef}
            visemes={visemes}
            mouthGated={mouthGated}
          />
          <div className="relative rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden min-h-[220px] md:min-h-0">
            <video ref={videoRef} playsInline className="w-full h-full object-cover" data-testid="mock-video-preview" />
            <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-zinc-950/85 to-transparent pointer-events-none" />
            <div className="absolute left-3 bottom-2 text-[11px] font-medium text-zinc-300">You</div>
            <div className="absolute right-2 bottom-2"><MicLevelMeter stream={stream} /></div>
            {recording && (
              <div
                data-testid="mock-rec-chip"
                className={`absolute top-3 left-3 inline-flex items-center gap-2 text-xs text-white rounded-full px-3 py-1 font-medium ${
                  elapsed >= 300 ? "bg-amber-500/95" : elapsed >= 240 ? "bg-amber-500/85" : "bg-rose-500/90"
                }`}
              >
                <Circle className="h-2 w-2 fill-white animate-pulse" /> REC · {fmtTime(elapsed)}
              </div>
            )}
            {!stream && !permissionErr && (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Requesting camera…
              </div>
            )}
            {permissionErr && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 text-sm p-6 text-center bg-zinc-950/90">
                <Video className="h-8 w-8 text-zinc-600 mb-3" />
                <div>{permissionErr}</div>
                <button
                  onClick={startCamera}
                  data-testid="mock-retry-perm"
                  className="mt-4 text-xs border border-zinc-800 rounded-md px-3 py-1.5 hover:bg-zinc-900"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current question, big card */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] via-zinc-900/50 to-zinc-950/70 p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <span className="h-10 w-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Volume2 className={`h-4 w-4 ${ttsPlaying ? "text-amber-400 animate-pulse" : "text-amber-400"}`} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-2 flex-wrap">
              Batch {currentBatch?.id} · {currentBatch?.label}
              {currentTurn?.status === "answered" && (
                <span className="text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-500/30 bg-emerald-500/[0.06] rounded-full px-1.5 py-0.5">
                  Completed{typeof currentTurn.score === "number" ? ` · ${currentTurn.score}/100` : ""}
                </span>
              )}
            </div>
            <div className="mt-2 text-lg text-zinc-100 leading-relaxed" data-testid="mock-current-question">
              {currentTurn?.question}
            </div>
            {currentTurn?.status === "answered" && currentTurn?.tip && (
              <div data-testid="mock-turn-tip" className="mt-3 text-xs text-amber-200/90 bg-amber-500/[0.06] border border-amber-500/25 rounded-lg px-3 py-2 leading-relaxed">
                Coach tip: {currentTurn.tip}
              </div>
            )}
          </div>
        </div>
        <audio
          ref={audioRef}
          onPlaying={() => setTtsPlaying(true)}
          onPause={() => setTtsPlaying(false)}
          onEnded={() => setTtsPlaying(false)}
          onError={() => setTtsPlaying(false)}
          className="hidden"
        />
      </div>

      {/* Recording controls + guidance */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {!recording && !uploading && !failedUpload && currentTurn && (
              <button
                onClick={startRecording}
                disabled={!stream}
                data-testid="mock-record-btn"
                className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-60"
              >
                <Circle className="h-3 w-3 fill-white" />
                {currentTurn.status === "answered" ? "Re-record this answer" : "Start recording"}
              </button>
            )}
            {recording && (
              <button
                onClick={stopRecording}
                data-testid="mock-stop-btn"
                className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 font-semibold rounded-lg px-4 py-2 text-sm"
              >
                <Square className="h-3 w-3 fill-zinc-950" /> Stop &amp; submit
              </button>
            )}
            {uploading && <UploadStages phase={upPhase} pct={upPct} />}
          </div>
          {(uploadErr || recErr) && !uploading && (
            <div data-testid="mock-recorder-error" className="rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{uploadErr || recErr}</span>
              </div>
              {failedUpload && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => doUpload(failedUpload)}
                    data-testid="mock-retry-upload-btn"
                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 text-xs"
                  >
                    <RefreshCw className="h-3 w-3" /> Retry upload
                  </button>
                  <button
                    onClick={() => { setFailedUpload(null); setUploadErr(""); }}
                    data-testid="mock-rerecord-btn"
                    className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-200 hover:bg-zinc-900 rounded-md px-3 py-1.5 text-xs"
                  >
                    Re-record instead
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 text-[11px] text-zinc-500">
          Tip: aim for 60–120 seconds per answer using STAR — Situation, Task, Action, Result.
          Practice only the questions you want, in any order.
        </div>
      </div>

      {/* Batch question navigator */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {BATCH_META.map((b) => {
          const batchTurns = turns.filter((t) => (t.batch || 1) === b.id);
          const done = batchTurns.filter((t) => t.status === "answered").length;
          const scores = batchTurns
            .filter((t) => t.status === "answered" && typeof t.score === "number")
            .map((t) => t.score);
          const avg = scores.length ? Math.round(scores.reduce((a, v) => a + v, 0) / scores.length) : null;
          return (
            <div key={b.id} data-testid={`mock-batch-${b.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-3">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                  Batch {b.id} · {b.label}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {avg !== null && (
                    <span
                      data-testid={`mock-batch-${b.id}-avg`}
                      title={`Average of ${scores.length} scored answer${scores.length === 1 ? "" : "s"} in this batch`}
                      className={`text-[10px] font-semibold tabular-nums rounded-full border px-1.5 py-0.5 ${
                        avg >= 70
                          ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/[0.06]"
                          : avg >= 50
                          ? "text-amber-300 border-amber-500/30 bg-amber-500/[0.06]"
                          : "text-rose-300 border-rose-500/30 bg-rose-500/[0.06]"
                      }`}
                    >
                      avg {avg}
                    </span>
                  )}
                  <div className="text-[11px] text-zinc-500 tabular-nums" data-testid={`mock-batch-${b.id}-progress`}>
                    {done}/{batchTurns.length}
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                {batchTurns.map((t, pos) => {
                  const isSel = t.index === selectedIdx;
                  const isDone = t.status === "answered";
                  return (
                    <button
                      key={t.index}
                      type="button"
                      disabled={recording || uploading}
                      onClick={() => setSelectedIdx(t.index)}
                      data-testid={`mock-q-${t.index}`}
                      className={`w-full text-left flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-60 ${
                        isSel
                          ? "border-amber-500/60 bg-amber-500/10 text-amber-100"
                          : isDone
                          ? "border-emerald-500/25 bg-emerald-500/[0.04] text-zinc-300 hover:border-emerald-500/40"
                          : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                      }`}
                    >
                      {isDone ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full border border-zinc-700 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">Q{pos + 1} · {t.question}</span>
                      {isDone && typeof t.score === "number" && (
                        <span className="text-[10px] font-semibold text-emerald-300 tabular-nums flex-shrink-0">{t.score}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

/* -------------------------- Report + Replay -------------------------- */

function ScoreBar({ label, value }) {  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-100 font-semibold tabular-nums">{v}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-amber-300 rounded-full transition-all"
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}

function PersonaLensPanel({ report }) {
  const pe = report?.persona_evaluation;
  if (!pe || (!pe.strengths?.length && !pe.concerns?.length && !pe.verdict)) return null;
  return (
    <div data-testid="persona-lens-panel" className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-6">
      <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1">Your Interviewer&apos;s Perspective</div>
      <div className="text-sm text-zinc-100 font-medium" data-testid="persona-lens-name">{pe.persona_name}</div>
      {pe.criteria?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {pe.criteria.map((c, i) => (
            <span key={i} className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-700 bg-zinc-950 rounded-full px-2 py-0.5">{c}</span>
          ))}
        </div>
      )}
      {pe.verdict && (
        <p className="text-sm text-zinc-300 leading-relaxed mt-3 italic" data-testid="persona-lens-verdict">&ldquo;{pe.verdict}&rdquo;</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {pe.strengths?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1.5">What impressed this interviewer</div>
            <ul className="space-y-1 text-sm text-zinc-200">
              {pe.strengths.map((s, i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />{s}</li>
              ))}
            </ul>
          </div>
        )}
        {pe.concerns?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-rose-400 mb-1.5">What concerned this interviewer</div>
            <ul className="space-y-1 text-sm text-zinc-200">
              {pe.concerns.map((s, i) => (
                <li key={i} className="flex gap-2"><AlertTriangle className="h-3.5 w-3.5 text-rose-400 mt-0.5 flex-shrink-0" />{s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function DynamicCompetencyPanel({ report }) {
  const scores = Array.isArray(report?.dynamic_competency_scores) ? report.dynamic_competency_scores : [];
  const [expanded, setExpanded] = useState(null);
  const status = report?.enrichment_status;

  // Pending - the background enrichment worker hasn't finished yet.
  if (!scores.length && status === "pending") {
    return (
      <div
        data-testid="dynamic-competency-pending"
        className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.03] via-zinc-900/40 to-zinc-950/60 p-6"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">
              Generating Evidence-Based Report
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              Building your custom blueprint and grading each answer against it. Usually
              20&ndash;40 seconds. You can safely leave this page and come back.
            </div>
          </div>
        </div>
        <div className="mt-5 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded-xl border border-zinc-800/70 bg-zinc-900/30 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!scores.length) return null;
  const blueprint = report?.blueprint || {};
  const notEvaluated = Array.isArray(report?.not_evaluated_competencies) ? report.not_evaluated_competencies : [];
  const summary = report?.evidence_based_summary || {};
  const recommendation = summary.hiring_recommendation;
  const rationale = summary.recommendation_rationale;

  return (
    <div
      data-testid="dynamic-competency-panel"
      className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.03] via-zinc-900/40 to-zinc-950/60 p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1">
            Blueprint-Driven Competency Report
          </div>
          <div className="text-sm text-zinc-300">
            <span className="text-zinc-100 font-medium">
              {blueprint.target_role || "Custom Blueprint"}
            </span>
            {blueprint.seniority ? <span className="text-zinc-500"> · {blueprint.seniority}</span> : null}
            {blueprint.industry ? <span className="text-zinc-500"> · {blueprint.industry}</span> : null}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Weighted, evidence-based competency scoring, derived from your resume and this JD.
          </div>
        </div>
        {recommendation && (
          <div
            data-testid="evidence-recommendation"
            className="inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/[0.06] text-amber-200 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap"
          >
            {recommendation}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {scores.map((c, i) => {
          const key = `${c.competency}-${i}`;
          const isOpen = expanded === key;
          const s = Math.max(0, Math.min(100, Number(c.score) || 0));
          const tone = s >= 80 ? "emerald" : s >= 60 ? "amber" : "rose";
          const toneRing = {
            emerald: "border-emerald-500/25 hover:border-emerald-500/40",
            amber: "border-amber-500/25 hover:border-amber-500/40",
            rose: "border-rose-500/25 hover:border-rose-500/40",
          }[tone];
          const barColor = { emerald: "bg-emerald-400", amber: "bg-amber-400", rose: "bg-rose-400" }[tone];
          const scoreColor = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300" }[tone];
          return (
            <button
              key={key}
              type="button"
              onClick={() => setExpanded(isOpen ? null : key)}
              data-testid={`dynamic-competency-row-${i}`}
              className={`w-full text-left rounded-xl border ${toneRing} bg-zinc-900/40 p-4 transition-colors`}
            >
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {c.competency || "Competency"}
                    </div>
                    {c.weight_pct ? (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        weight {c.weight_pct}%
                      </span>
                    ) : null}
                  </div>
                  <div className="h-1.5 w-full bg-zinc-800 rounded overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${s}%` }} />
                  </div>
                </div>
                <div className={`text-lg font-semibold tabular-nums ${scoreColor}`}>{s}</div>
                <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </div>
              {isOpen && (
                <div
                  data-testid={`dynamic-competency-detail-${i}`}
                  className="mt-3 pt-3 border-t border-zinc-800/70 space-y-2 text-sm text-zinc-300"
                >
                  {c.evidence && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Evidence</div>
                      <div className="leading-relaxed">{c.evidence}</div>
                    </div>
                  )}
                  {c.improvement_opportunity && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">
                        Improvement opportunity
                      </div>
                      <div className="leading-relaxed">{c.improvement_opportunity}</div>
                    </div>
                  )}
                  {c.stronger_answer_would_include && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">
                        A stronger answer would include
                      </div>
                      <div className="leading-relaxed">{c.stronger_answer_would_include}</div>
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {notEvaluated.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-800/70">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Not evaluated in this interview
          </div>
          <div className="flex flex-wrap gap-1.5">
            {notEvaluated.map((n, i) => (
              <span
                key={i}
                className="text-[11px] text-zinc-400 border border-zinc-800 bg-zinc-900/50 rounded-full px-2.5 py-0.5"
              >
                {n}
              </span>
            ))}
          </div>
        </div>
      )}

      {rationale && (
        <div
          data-testid="evidence-recommendation-rationale"
          className="mt-4 pt-4 border-t border-zinc-800/70 text-sm text-zinc-300 leading-relaxed"
        >
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">
            Recommendation rationale
          </div>
          {rationale}
        </div>
      )}
    </div>
  );
}


function ProofOfImprovementPanel({ proof }) {
  if (!proof || !proof.competencies?.length) return null;
  const prevScore = proof?.previous?.interview_score;
  const curScore = proof?.current?.interview_score;
  const delta = proof.delta || 0;
  const positive = delta >= 0;
  return (
    <div
      data-testid="proof-of-improvement"
      className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-6"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-1">Proof of Improvement</div>
          <div className="text-xs text-zinc-400">
            Compared to your last mock{proof?.previous?.job_title ? ` · ${proof.previous.job_title}` : ""}.
          </div>
        </div>
        <div
          data-testid="proof-delta-chip"
          className={`inline-flex items-center gap-1.5 text-xs font-semibold tabular-nums px-2.5 py-1 rounded-md border ${
            positive
              ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/[0.06]"
              : "text-rose-300 border-rose-500/25 bg-rose-500/[0.05]"
          }`}
        >
          {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
          {positive ? `+${delta}` : `${delta}`} overall
        </div>
      </div>

      {/* Big before → after */}
      <div className="flex items-center gap-6 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Previous</div>
          <div data-testid="proof-previous" className="text-2xl font-bold text-zinc-300 tabular-nums leading-none">
            {prevScore ?? "–"}
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-zinc-600" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-1">Current</div>
          <div data-testid="proof-current" className={`text-3xl font-bold tabular-nums leading-none ${positive ? "text-emerald-300" : "text-rose-300"}`}>
            {curScore ?? "–"}
          </div>
        </div>
      </div>

      {/* Per-competency deltas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 pt-4 border-t border-zinc-800/60">
        {proof.competencies.filter((c) => c.key !== "interview_score").map((c) => {
          const isPositive = c.delta > 0;
          const isNegative = c.delta < 0;
          const isFlat = c.delta === 0;
          return (
            <div key={c.key} className="flex items-center justify-between text-sm" data-testid={`proof-competency-${c.key}`}>
              <span className="text-zinc-300">{c.label}</span>
              <span className="tabular-nums text-zinc-400 flex items-center gap-2">
                <span className="text-zinc-500">{c.previous}</span>
                <ArrowRight className="h-3 w-3 text-zinc-700" />
                <span className="text-zinc-100 font-semibold">{c.current}</span>
                <span
                  className={`inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-md px-1.5 py-0.5 min-w-[36px] justify-center ${
                    isPositive
                      ? "text-emerald-300 bg-emerald-500/10 border border-emerald-500/20"
                      : isNegative
                      ? "text-rose-300 bg-rose-500/10 border border-rose-500/20"
                      : "text-zinc-500 bg-zinc-800 border border-zinc-700"
                  }`}
                >
                  {isFlat ? "0" : (isPositive ? `+${c.delta}` : `${c.delta}`)}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ReportPanel({ session, onNew, onSessionUpdate }) {
  const r = session.report || {};
  const rec = RECOMMENDATION[r.hiring_recommendation] || RECOMMENDATION.needs_practice;
  const recClass = RECOMMENDATION_CLASSES[rec.color];
  const [attempts, setAttempts] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenError, setRegenError] = useState(null);
  const [softTimedOut, setSoftTimedOut] = useState(false);

  // Fire `replay_opened` exactly once per report-view mount. This is the
  // signal the Product tab counts for "Replays Opened". Guarded by a ref so
  // parent re-renders don't spam the analytics endpoint.
  const replayEventSent = useRef(false);
  useEffect(() => {
    if (replayEventSent.current || !session?.session_id) return;
    replayEventSent.current = true;
    trackEvent(AnalyticsEvent.REPLAY_OPENED, {
      session_id: session.session_id,
      job_title: session.job_title,
    });
  }, [session?.session_id, session?.job_title]);

  // Fire `interview_feedback_viewed` once the enrichment pipeline lands and
  // the user is actually seeing coaching feedback (not the "generating…"
  // spinner). Also once-only per mount to keep the event clean.
  const feedbackEventSent = useRef(false);
  useEffect(() => {
    if (feedbackEventSent.current) return;
    if (!session?.session_id) return;
    if (r.enrichment_status !== "ready") return;
    feedbackEventSent.current = true;
    trackEvent(AnalyticsEvent.INTERVIEW_FEEDBACK_VIEWED, {
      session_id: session.session_id,
      readiness_score: r.readiness_score ?? null,
    });
  }, [session?.session_id, r.enrichment_status, r.readiness_score]);

  // Poll for evidence-based enrichment while it is still pending. Owns its
  // own effect so both Practice and Official AI Interview pages get it.
  useEffect(() => {
    const status = r.enrichment_status;
    if (!session?.session_id || status !== "pending") return undefined;
    if (!onSessionUpdate) return undefined;
    let cancelled = false;
    let attempts_ = 0;
    // Faster polling: 2s interval → ~4 min budget = 120 attempts. This shaves
    // up to ~6s off the perceived wait vs. the previous 4s cadence.
    const maxAttempts = 120;
    setSoftTimedOut(false);
    // Soft timeout - after 3 minutes still pending, offer a manual retry
    // even without an explicit failure.
    const softTimer = setTimeout(() => {
      if (!cancelled) setSoftTimedOut(true);
    }, 180000);
    const tick = async () => {
      if (cancelled) return;
      attempts_ += 1;
      try {
        const { data } = await api.get(`/video-interview/${session.session_id}`);
        if (cancelled) return;
        const next = data?.report?.enrichment_status;
        if (data) onSessionUpdate(data);
        if (next && next !== "pending") return;
      } catch { /* transient, retry */ }
      // SPEED: sub-second polling while the report is hot (first ~7s), then
      // back off to 2s. The pipeline usually lands scores in 3-5s, so tight
      // early polling shaves ~1s off perceived latency at negligible cost.
      const delay = attempts_ < 10 ? 700 : 2000;
      if (!cancelled && attempts_ < maxAttempts) setTimeout(tick, delay);
    };
    const t = setTimeout(tick, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
      clearTimeout(softTimer);
    };
  }, [session?.session_id, r.enrichment_status, onSessionUpdate]);

  // Manual retry - hits POST /video-interview/{sid}/regenerate-report and
  // re-enters the pending flow. Used by "Retry Report Generation" button.
  const retryReport = async () => {
    if (!session?.session_id || regenBusy) return;
    setRegenBusy(true);
    setRegenError(null);
    try {
      const { data } = await api.post(
        `/video-interview/${session.session_id}/regenerate-report`,
      );
      if (data && onSessionUpdate) onSessionUpdate(data);
      setSoftTimedOut(false);
    } catch (e) {
      setRegenError(
        formatApiError(e?.response?.data?.detail) ||
        "Couldn't restart report generation. Please try again in a moment.",
      );
    } finally {
      setRegenBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/video-interview/compare", {
          params: session.job_title ? { role: session.job_title } : {},
        });
        setAttempts(data.attempts || []);
      } catch { /* noop */ }
    })();
  }, [session.job_title]);

  const [activeTurn, setActiveTurn] = useState(0);
  const videoRef = useRef(null);
  const turns = session.turns || [];
  const currentTurn = turns[activeTurn];

  const jumpTo = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play().catch(() => {});
    }
  };

  const showRetryUI = r.enrichment_status === "failed" || softTimedOut;
  const interviewMilestone = buildInterviewCompleteMilestone(session);

  return (
    <div data-testid="video-report" className="max-w-5xl space-y-8">
      <ShareAssessmentModal
        open={showShare}
        onClose={() => setShowShare(false)}
        sessionId={session.session_id}
      />

      {/* Career Milestone celebration, only when the report is ready */}
      {interviewMilestone && <CareerMilestoneBanner milestone={interviewMilestone} />}

      {/* Retry surface, shown when the pipeline reports 'failed' OR when
          it has been 'pending' for over 3 minutes (soft timeout). Never
          leaves the user staring at an infinite spinner. */}
      {showRetryUI && (
        <div
          data-testid="report-retry-card"
          className="rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-5 flex flex-wrap items-start justify-between gap-4"
        >
          <div className="flex-1 min-w-[280px]">
            <div className="text-sm text-zinc-100 font-semibold">
              {r.enrichment_status === "failed"
                ? "Report generation ran into an issue."
                : "This is taking longer than usual."}
            </div>
            <div className="text-[13px] text-zinc-400 mt-1 leading-relaxed">
              {r.enrichment_error
                || "The AI service may be slow or unavailable. Your interview transcript is safe, click Retry to regenerate the report."}
            </div>
            {regenError && (
              <div className="text-[12px] text-rose-300 mt-2">{regenError}</div>
            )}
          </div>
          <button
            type="button"
            onClick={retryReport}
            disabled={regenBusy}
            data-testid="retry-report-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed px-3.5 py-2 text-sm font-medium transition-colors"
          >
            {regenBusy ? (
              <><Loader2 className="h-4 w-4 animate-spin" />Regenerating&hellip;</>
            ) : (
              <><Sparkles className="h-4 w-4" />Retry Report Generation</>
            )}
          </button>
        </div>
      )}

      {/* Live progress, mirrors the real pipeline stages while background
          LLM work runs. "Scoring" until core scores land (legacy_ready),
          then "Building evidence" until the full report is ready. */}
      {r.enrichment_status === "pending" && !showRetryUI && (
        <div
          data-testid="report-pending-banner"
          className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-zinc-900/40 to-zinc-950/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <div className="flex-1">
            <div className="text-sm text-zinc-100 font-semibold">
              {r.legacy_ready
                ? "Scores are in, building your evidence-based breakdown…"
                : "Generating your interview report…"}
            </div>
            <div className="text-[13px] text-zinc-400 mt-0.5 leading-relaxed">
              Usually under 10 seconds. You can safely leave this page. We&apos;ll
              save the report to your account.
            </div>
          </div>
          <ReportProgressSteps stage={r.legacy_ready ? "evidence" : "scoring"} />
        </div>
      )}

      {/* Header + recommendation */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1" data-testid="report-scope-label">
            {session.assessment_scope?.label || "Executive Interview Report"}
          </div>
          <h2 className="text-2xl font-bold text-zinc-50 tracking-tight">
            {session.job_title || "Video Interview"}
            {session.company ? <span className="text-zinc-500"> · {session.company}</span> : null}
          </h2>
          {session.assessment_scope && (
            <div className="text-[11px] text-zinc-500 mt-1" data-testid="report-scope-note">
              Based on {session.assessment_scope.core_answered} completed core response
              {session.assessment_scope.core_answered === 1 ? "" : "s"}
              {session.assessment_scope.complete
                ? "."
                : ` of ${session.assessment_scope.core_total} — this is not a final assessment.`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowShare(true)}
            data-testid="video-share-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-amber-500/40 text-amber-300 hover:text-amber-200 hover:border-amber-400/60 hover:bg-amber-500/[0.06] rounded-full px-3.5 py-2 font-medium transition-colors"
          >
            <Share2 className="h-3 w-3" />
            Share Assessment
          </button>
          <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-2 text-sm font-semibold ${recClass}`} data-testid="video-recommendation">
            <span className="text-base leading-none">{rec.dot}</span> {rec.label}
          </div>
        </div>
      </div>

      {/* Big scores — Readiness is the headline, Performance supporting */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-zinc-900/40 to-zinc-950/60 p-5">
          <div className="flex items-center gap-1.5">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Interview Readiness Score</div>
            <ScoreInfoTip variant="readiness" />
          </div>
          <div className="mt-1 text-4xl font-bold text-zinc-50">{r.readiness_score ?? "–"}<span className="text-lg text-zinc-500">/100</span></div>
          <div className="text-[11px] text-zinc-500 mt-1 leading-snug">How ready you are for the real interview: JD alignment &amp; evidence.</div>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="flex items-center gap-1.5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interview Performance</div>
            <ScoreInfoTip variant="overall" />
          </div>
          <div className="mt-1 text-4xl font-bold text-zinc-50">{r.overall_score ?? "–"}<span className="text-lg text-zinc-500">/100</span></div>
          <div className="text-[11px] text-zinc-500 mt-1 leading-snug">How you performed in this session: delivery, clarity, structure.</div>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Speech Pace</div>
          <div className="mt-1 text-4xl font-bold text-zinc-50">{r.speech_pace_wpm ?? 0}<span className="text-lg text-zinc-500"> wpm</span></div>
          <div className="text-[11px] text-zinc-500 mt-1">{r.filler_words_count ?? 0} filler words</div>
        </div>
      </div>

      {/* Sub-scores */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="text-sm font-semibold text-zinc-50 mb-4">Competency Breakdown</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          <ScoreBar label="Communication" value={r.communication} />
          <ScoreBar label="Confidence" value={r.confidence} />
          <ScoreBar label="Answer Structure" value={r.answer_structure} />
          <ScoreBar label="Job Fit" value={r.job_fit} />
          <ScoreBar label="Leadership Presence" value={r.leadership_presence} />
          <ScoreBar label="Executive Presence" value={r.executive_presence} />
          {r.star_score == null ? (
            <div data-testid="star-na-row" className="flex items-center justify-between">
              <span className="text-xs text-zinc-400">STAR Framework</span>
              <span className="text-[10px] text-zinc-500">N/A — no behavioral questions</span>
            </div>
          ) : (
            <ScoreBar label="STAR Framework" value={r.star_score} />
          )}
        </div>
        <p data-testid="scoring-disclosure" className="mt-4 pt-3 border-t border-zinc-800/70 text-[11px] text-zinc-500 leading-relaxed">
          Scores are based on your transcribed answers plus speech metrics like duration, pace, and
          filler words. Intuiterview does not analyze facial expressions, eye contact, appearance, or
          emotions — video is used for interview simulation, recording, and replay only. Confidence and
          Presence scores reflect the content and delivery of your words, not facial or emotion analysis.
        </p>
      </div>

      {/* Dynamic Blueprint-Driven Competency Breakdown (Engine v2 Phase 3) */}
      <DynamicCompetencyPanel report={r} />

      {/* Persona-specific evaluation lens (Interview Intelligence Engine) */}
      <PersonaLensPanel report={r} />

      {/* Proof of Improvement, vs immediately previous mock */}
      {session.proof_of_improvement && <ProofOfImprovementPanel proof={session.proof_of_improvement} />}

      {/* Strengths / Improvements / Coaching */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-5">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Strengths</div>
          <ul className="space-y-1.5 text-sm text-zinc-200">
            {(r.strengths || []).map((s, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.03] p-5">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Improvement Areas</div>
          <ul className="space-y-1.5 text-sm text-zinc-200">
            {(r.improvements || []).map((s, i) => (
              <li key={i} className="flex gap-2"><TrendingUp className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />{s}</li>
            ))}
          </ul>
        </div>
      </div>

      {r.coaching && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Personalized Coaching</div>
          <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{r.coaching}</p>
        </div>
      )}

      {/* Evidence-based Actions Before Real Interview (Engine v2 Phase 3) */}
      {Array.isArray(r.evidence_based_actions) && r.evidence_based_actions.length > 0 && (
        <div
          data-testid="evidence-based-actions"
          className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-5"
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3">
            Top Actions Before Your Real Interview
          </div>
          <ol className="space-y-2 text-sm text-zinc-200">
            {r.evidence_based_actions.map((a, i) => (
              <li
                key={`action-${i}-${(a || "").slice(0, 40)}`}
                data-testid={`evidence-action-${i}`}
                className="flex gap-3 leading-relaxed"
              >
                <span className="flex-shrink-0 mt-0.5 h-5 w-5 rounded-full border border-amber-500/40 text-amber-300 text-[11px] font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span>{a}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* One-question contextual feedback, only after the report is ready,
          never during recording. Slow-wait prompt takes precedence. */}
      {r.legacy_ready && (() => {
        const slowTurn = (turns || []).find((t) => (t.upload_transcribe_ms || 0) > 15000);
        return slowTurn ? (
          <ContextualFeedback
            type="contextual_slow_wait"
            entityKey={session.session_id}
            question="That took longer than expected. Was the wait acceptable?"
            options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
            context={{ session_id: session.session_id, upload_transcribe_ms: slowTurn.upload_transcribe_ms }}
          />
        ) : (
          <ContextualFeedback
            type="contextual_interview_helpful"
            entityKey={session.session_id}
            question="How helpful was this interview?"
            options={[{ label: "👍 Helpful", value: "helpful" }, { label: "👎 Not helpful", value: "not_helpful" }]}
            context={{ session_id: session.session_id }}
          />
        );
      })()}

      {r.legacy_ready && (
        <DeliverableFeedback
          deliverable="practice_interview"
          label="Practice Interview"
          entityKey={session.session_id}
          context={{ session_id: session.session_id }}
        />
      )}

      {/* Replay */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="video-replay">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-zinc-50">AI Interview Replay</div>
          <div className="flex flex-wrap gap-1.5">
            {turns.map((t, i) => (
              <button
                key={t?.turn_id || t?.question_id || `turn-${i}`}
                onClick={() => setActiveTurn(i)}
                data-testid={`video-turn-tab-${i}`}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors flex items-center gap-1.5 ${
                  activeTurn === i
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                    : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                Q{i + 1}
                {t.is_followup && (
                  <span className="text-[9px] uppercase tracking-widest text-sky-300">FU</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {currentTurn ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
            <div>
              <div className="text-xs text-zinc-400 mb-2">{currentTurn.question}</div>
              {currentTurn.video_url ? (
                <video
                  ref={videoRef}
                  key={currentTurn.video_url}
                  src={currentTurn.video_url}
                  controls
                  className="w-full rounded-lg border border-zinc-800 bg-black aspect-video"
                  data-testid="video-replay-player"
                />
              ) : (
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 h-40 flex items-center justify-center text-xs text-zinc-500">
                  No recording available for this turn.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-500 mt-2">
                <span>~{Math.round(currentTurn.duration || 0)}s</span>
                <span>{currentTurn.wpm || 0} wpm</span>
                <span>{currentTurn.filler_count || 0} fillers</span>
                {/* Speed Insights, reinforces the perception that Intuiterview
                    is fast without exposing internal timings. Only shows when
                    the backend actually measured the upload+transcribe cycle
                    for this turn (post Session 46) and the value is a sane
                    positive number. Sub-second is rounded to 1 decimal; longer
                    durations are rounded to whole seconds so the badge stays
                    calm and glanceable. */}
                {typeof currentTurn.upload_transcribe_ms === "number" &&
                  currentTurn.upload_transcribe_ms > 0 && (() => {
                    const secs = currentTurn.upload_transcribe_ms / 1000;
                    const label = secs < 10 ? `${secs.toFixed(1)}s` : `${Math.round(secs)}s`;
                    return (
                      <span
                        data-testid="speed-insights-badge"
                        title="Time to upload and transcribe this answer"
                        className="inline-flex items-center gap-1 text-emerald-400/70"
                      >
                        <span className="opacity-70">·</span> Uploaded &amp; transcribed in {label}
                      </span>
                    );
                  })()}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline notes</div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {(currentTurn.comments || []).length === 0 && (
                  <div className="text-xs text-zinc-500">No timeline notes for this turn.</div>
                )}
                {(currentTurn.comments || []).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => jumpTo(c.timestamp_seconds)}
                    data-testid={`video-comment-${activeTurn}-${i}`}
                    className={`w-full text-left rounded-lg border p-2.5 transition-colors hover:bg-zinc-900 ${TAG_STYLES[c.tag] || TAG_STYLES.improvement}`}
                  >
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest opacity-80">
                      <span>{c.tag}</span>
                      <span className="tabular-nums text-zinc-400"><Clock className="inline h-3 w-3 mr-1" />{fmtTime(c.timestamp_seconds)}</span>
                    </div>
                    <div className="text-xs mt-1 text-zinc-100">{c.note}</div>
                  </button>
                ))}
              </div>
              {currentTurn.transcript && (
                <details className="mt-4">
                  <summary className="text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-300">
                    Full transcript
                  </summary>
                  <div className="mt-2 text-xs text-zinc-300 leading-relaxed max-h-40 overflow-y-auto pr-1">
                    {currentTurn.transcript}
                  </div>
                </details>
              )}
            </div>
          </div>
        ) : (
          <div className="text-xs text-zinc-500">No turns to replay.</div>
        )}
      </div>

      {/* Compare attempts */}
      {attempts.length > 1 && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="video-compare">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-zinc-50">Attempts for this role</div>
            <div className="text-[11px] text-zinc-500">Track your improvement across sessions.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-right px-2 py-1.5">Overall</th>
                  <th className="text-right px-2 py-1.5">Readiness</th>
                  <th className="text-right px-2 py-1.5">Communication</th>
                  <th className="text-right px-2 py-1.5">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.session_id} className={`border-t border-zinc-800/70 ${a.session_id === session.session_id ? "bg-amber-500/[0.03]" : ""}`}>
                    <td className="px-2 py-1.5 text-zinc-400">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-2 py-1.5 text-right text-zinc-100 tabular-nums">{a.overall_score ?? "–"}</td>
                    <td className="px-2 py-1.5 text-right text-zinc-100 tabular-nums">{a.readiness_score ?? "–"}</td>
                    <td className="px-2 py-1.5 text-right text-zinc-300 tabular-nums">{a.communication ?? "–"}</td>
                    <td className="px-2 py-1.5 text-right text-zinc-300 tabular-nums">{a.confidence ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="pt-2 flex flex-wrap gap-3">
        <button
          onClick={onNew}
          data-testid="video-new-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 text-sm shadow-[0_0_20px_-6px_rgba(245,158,11,0.6)]"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Practice again
        </button>
        {session.cheat_sheet_id && (
          <Link
            to="/app/briefs"
            data-testid="video-open-brief"
            className="inline-flex items-center gap-2 text-sm border border-amber-500/30 text-amber-300 bg-amber-500/[0.06] hover:bg-amber-500/[0.1] rounded-lg px-4 py-2"
          >
            <Sparkles className="h-3.5 w-3.5" /> Open Interview Day Brief
          </Link>
        )}
        <Link
          to="/app/dashboard"
          className="inline-flex items-center gap-2 text-sm border border-zinc-800 text-zinc-100 hover:bg-zinc-900 rounded-lg px-4 py-2"
        >
          View updated readiness on Dashboard <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

/* -------------------------- Main page -------------------------- */

export default function VideoMockInterview() {
  const [entitlement, setEntitlement] = useState(null);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("setup"); // setup | record | report
  const [priorSessions, setPriorSessions] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { isFree, refresh: refreshEntitlement, track } = useEntitlement();

  // Enrichment polling now lives inside ReportPanel so both Practice and
  // Official AI Interview pages benefit from it.

  useEffect(() => {
    (async () => {
      try {
        const [e, list] = await Promise.all([
          api.get("/video-interview/entitlement"),
          api.get("/video-interview/list").catch(() => ({ data: [] })),
        ]);
        setEntitlement(e.data);
        setPriorSessions(list.data || []);
      } catch (e) {
        console.warn("[VideoMockInterview] entitlement/list load failed", e);
        setEntitlement({ premium: false });
      }
    })();
  }, []);

  // Render immediately — entitlement loads in the background (beta: everyone
  // is allowed; LockedPreview only once we KNOW the quota is exhausted).
  const canRun = !entitlement || entitlement.is_paid || entitlement.video?.allowed;
  if (!canRun) return <LockedPreview />;

  return (
    <div data-testid="video-page" className="animate-in">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">
            03 · Practice · {entitlement?.is_paid ? "Premium" : "Free during beta"}
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50">Mock Interview</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            15 questions in 3 batches of 5 — Introduction &amp; Motivation, Skills &amp; Experience,
            and Behavioral, Situational &amp; Role Fit. Practice any question, in any order, with
            AI coaching before the Official AI Interview.
          </p>
        </div>
        {phase !== "setup" && session && (
          <button
            onClick={() => { setSession(null); setPhase("setup"); }}
            data-testid="video-back-setup"
            className="hidden sm:inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-200"
          >
            <RefreshCw className="h-3 w-3" /> New session
          </button>
        )}
      </div>

      {phase === "setup" && !session && (
        <>
          <SetupPanel
            isPaid={!!entitlement?.is_paid}
            onStart={(s) => {
              setSession(s);
              setPhase("record");
              track("practice.started", { session_id: s?.session_id });
            }}
          />
          {priorSessions.length > 0 && (
            <div className="mt-10 max-w-2xl">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Recent sessions</div>
              <div className="space-y-2">
                {priorSessions.slice(0, 5).map((s) => (
                  <button
                    key={s.session_id}
                    data-testid={`video-recent-${s.session_id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/video-interview/${s.session_id}`);
                        setSession(data);
                        setPhase(data.status === "completed" ? "report" : "record");
                      } catch { /* noop */ }
                    }}
                    className="w-full text-left p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <MessageSquare className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-100 flex items-center gap-2 min-w-0">
                          <span className="truncate">{s.job_title || "Video session"}</span>
                          {s.is_sample && (
                            <span data-testid={`video-recent-sample-badge-${s.session_id}`} className="text-[9px] uppercase tracking-wider font-semibold text-sky-300 bg-sky-500/10 border border-sky-500/25 rounded px-1.5 py-0.5 flex-shrink-0">Sample</span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          {new Date(s.created_at).toLocaleString()} · {s.status}{s.total_questions ? ` · ${s.total_questions} question${s.total_questions === 1 ? "" : "s"}` : ""}
                        </div>
                      </div>
                    </div>
                    {s.overall_score !== null && s.overall_score !== undefined && (
                      <span className="text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-md px-2 py-1 flex-shrink-0">
                        {s.overall_score}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {phase === "record" && session && (
        session.flexible ? (
          <FlexibleRecorderPanel
            session={session}
            onAbort={() => { setSession(null); setPhase("setup"); }}
            onSession={(s) => {
              setSession(s);
              if (s?.status === "completed") {
                setPhase("report");
                track("practice.completed", { session_id: s?.session_id, is_first: isFree });
              }
            }}
          />
        ) : (
          <RecorderPanel
            session={session}
            onSession={(s) => setSession(s)}
            onFinalize={(s) => {
              setSession(s);
              setPhase("report");
              track("practice.completed", {
                session_id: s?.session_id,
                is_first: isFree,
              });
            }}
          />
        )
      )}

      {phase === "report" && session && (
        isFree ? (
          <FreeSummaryScreen
            report={session.report || {}}
            onContinue={() => window.location.assign("/app/upgrade")}
          />
        ) : (
          <ReportPanel
            session={session}
            onNew={() => { setSession(null); setPhase("setup"); }}
            onSessionUpdate={(s) => setSession(s)}
          />
        )
      )}

      <UpgradeLiveModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        onActivated={async () => {
          track("upgrade.purchase_completed", { source: "free_summary_video" });
          await refreshEntitlement();
          setShowUpgrade(false);
        }}
      />
    </div>
  );
}
